import { Formik, FormikConfig, FormikErrors } from "formik";
import { isUndefined, omitBy } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { SignupIntro, SignupSuccess } from "./SignupPageSections";
import { CONTACT_EMAIL } from "utils/constants";
import { Spinner, Button, Input, Banner, typography } from "@dsaui/components";
import routes from "routes";
import { useAppContext } from "utils/hooks";
import { validateEmail } from "utils/validators";

interface FormValues {
  email: string;
}

interface SignupPageContentProps {
  onSignInClick: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
  ready: boolean;
  signupSuccess: boolean;
}

const SignupPage = () => {
  const { api, authenticated, toggleNotification, ready, keycloak } = useAppContext();
  const { t } = useTranslation();
  const [signupSuccess, setSignupSuccess] = useState(false);
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    routes.internal.base;

  const onSubmit = async (values: FormValues): Promise<void> => {
    if (api) {
      const { email } = values;

      const [err] = await api.createUser({
        email,
      });

      if (err) {
        toggleNotification({
          message: t(`errors.server.${err.type}.${err.key}`, {
            email: CONTACT_EMAIL,
          }),
          variant: "error",
        });

        return;
      }
      setSignupSuccess(true);
    } else {
      toggleNotification({
        message: t("errors.server.http.networkError"),
        variant: "error",
      });
    }
  };

  if (authenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <SignupPageContent
      onSignInClick={() => keycloak.login()}
      onSubmit={onSubmit}
      ready={ready}
      signupSuccess={signupSuccess}
    />
  );
};

export const SignupPageContent = ({
  onSignInClick,
  onSubmit,
  ready,
  signupSuccess,
}: SignupPageContentProps) => {
  const { t } = useTranslation();

  const validateForm = (values: FormValues): FormikErrors<FormValues> => {
    return omitBy(
      {
        email: validateEmail(values),
      },
      isUndefined,
    );
  };

  const submitForm: FormikConfig<FormValues>["onSubmit"] = async (
    values,
    { setSubmitting },
  ) => {
    setSubmitting(true);
    try {
      await onSubmit(values);
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  return (
    <>
      <SignupIntro onSignInClick={onSignInClick} />
      <div className="border-t border-gray-200 bg-white px-4 py-5 sm:px-6 sm:py-8">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
          {signupSuccess ? (
            <SignupSuccess />
          ) : (
            <>
              <Banner
                bodyClassName={typography.notificationWarning.className}
                variant="warning"
              >
                {t("signup.betaNotice")}
              </Banner>
              <Formik<FormValues>
                initialValues={{
                  email: "",
                }}
                validate={validateForm}
                onSubmit={submitForm}
                validateOnMount
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  isValid,
                }) => {
                  return (
                    <form aria-label="signup form" onSubmit={handleSubmit}>
                      <Input
                        autoComplete="email"
                        id="email"
                        name="email"
                        type="email"
                        errorMessage={
                          touched.email
                            ? errors.email
                              ? t(errors.email)
                              : undefined
                            : undefined
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        label={t("forms.labels.email")}
                        placeholder={t("signup.emailPlaceholder")}
                        subtitle={t("signup.emailHelp")}
                        required
                      />
                      <div>
                        {!isSubmitting && (
                          <Button
                            block
                            className="mt-1"
                            type="submit"
                            disabled={!isValid || !ready}
                          >
                            {t("signup.actions.continue")}
                          </Button>
                        )}
                        {isSubmitting && <Spinner className="mt-1" />}
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SignupPage;
